/**
 * CategoryTagEditor — Inline editable category tag chips.
 *
 * Uses Ant Design Select in "tags" mode styled as color-coded chips.
 * - `value`: current tag(s) for this objective
 * - `onChange`: called with updated tags array
 * - `catalog`: suggested labels from the session's category catalog
 * - `disabled`: renders as read-only chips when true
 */

import { Select, Tag } from "antd";
import { getCategoryStyle, getCategoryLabel } from "pages/Modus/RoadmapBuilder/components/BlockDetailView";

interface CategoryTagEditorProps {
  value: string[];
  onChange: (tags: string[]) => void;
  catalog?: string[];
  disabled?: boolean;
  maxCount?: number;
}

export function CategoryTagEditor({
  value,
  onChange,
  catalog = [],
  disabled = false,
  maxCount = 3,
}: CategoryTagEditorProps) {
  // Build options from catalog, deduped with current values
  const allLabels = Array.from(new Set([...value, ...catalog]));
  const options = allLabels.map((label) => ({ value: label, label: getCategoryLabel(label) }));

  if (disabled) {
    return (
      <div className="flex flex-wrap gap-1">
        {value.map((tag) => {
          const style = getCategoryStyle(tag);
          return (
            <Tag
              key={tag}
              style={{
                background: style.headerBg,
                borderColor: style.border,
                color: style.text,
                fontSize: 11,
                fontWeight: 600,
                margin: 0,
              }}
            >
              {getCategoryLabel(tag)}
            </Tag>
          );
        })}
      </div>
    );
  }

  return (
    <Select
      mode="tags"
      value={value}
      onChange={(tags) => {
        if (maxCount === 1) {
          // Single-tag mode: newest selection replaces the old one
          const newest = tags.length > 0 ? [tags[tags.length - 1]] : [];
          onChange(newest);
        } else {
          onChange(tags.slice(0, maxCount));
        }
      }}
      options={options}
      placeholder="Add category..."
      size="small"
      variant="borderless"
      style={{ minWidth: 120, fontSize: 11 }}
      maxTagCount="responsive"
      tagRender={({ label: tagLabel, value: tagValue, closable, onClose }) => {
        const style = getCategoryStyle(String(tagValue));
        return (
          <Tag
            closable={closable}
            onClose={onClose}
            style={{
              background: style.headerBg,
              borderColor: style.border,
              color: style.text,
              fontSize: 11,
              fontWeight: 600,
              marginRight: 3,
            }}
          >
            {tagLabel}
          </Tag>
        );
      }}
    />
  );
}
